import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { message, Spin } from 'antd';
import {
  ChevronRightIcon,
  ExternalLinkIcon,
  LeftTailArrowIcon,
} from '@/assets/icons/icons';
import type {
  HelpArticleReferrer,
  HelpArticleViewProps,
  HelpOpenPath,
} from '../../types';
import styles from '../../AppHelpCenter.module.scss';
import { useHelpArticle, useHelpEvent } from '../../hooks';
import HelpArticleBlock from '../HelpArticleBlock/HelpArticleBlock';
import HelpArticleFeedback from '../HelpArticleFeedback/HelpArticleFeedback';
import HelpArticlePager from '../HelpArticlePager/HelpArticlePager';

const HelpArticleView = ({
  articleId,
  referrer,
  onOpenArticle,
  onOpenCategory,
  onAskAssistant,
  aiEnabled,
}: HelpArticleViewProps) => {
  const { data, loading, error, errorMessage, outcome } =
    useHelpArticle(articleId);
  const navigate = useNavigate();
  const topRef = useRef<HTMLDivElement>(null);
  const trackEvent = useHelpEvent();

  // PRD: the body scrolls to top when the article changes.
  useEffect(() => {
    topRef.current?.scrollIntoView({ block: 'start' });
  }, [articleId]);

  // Telemetry once the article loads: shared links send their outcome,
  // other entry points send a view. Sources without a referrer send nothing.
  useEffect(() => {
    if (loading || !outcome || !referrer) return;
    if (referrer === 'link') {
      trackEvent({
        event: 'help_article_link_open',
        props: { article_id: articleId, outcome },
      });
    } else if (data) {
      trackEvent({
        event: 'help_article_view',
        props: {
          article_id: articleId,
          referrer: referrer as HelpArticleReferrer,
        },
      });
    }
    // Fires once per loaded article.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, outcome]);

  if (loading) {
    return (
      <div className={styles.categoryState} ref={topRef}>
        <Spin />
      </div>
    );
  }

  if (error || !data) {
    // Backend says why (e.g. "This article is not available for your role").
    return (
      <div className={styles.categoryState} ref={topRef}>
        {errorMessage ?? 'Could not load this article. Please try again.'}
      </div>
    );
  }

  // Routes come resolved from the backend. PRD: Help stays open and toasts "Opened {path}".
  const goTo = ({ label, route }: HelpOpenPath) => {
    const _ = navigate(route);
    message.success(`Opened ${label}`);
    trackEvent({ event: 'help_deeplink', props: { path: route } });
  };

  return (
    <article className={styles.article} ref={topRef}>
      <nav className={styles.articleBreadcrumb} aria-label='Breadcrumb'>
        {data.category_id ? (
          <button
            type='button'
            className={styles.articleBreadcrumbLink}
            onClick={() =>
              onOpenCategory({ id: data.category_id!, name: data.category })
            }
          >
            {data.category}
          </button>
        ) : (
          <span>{data.category}</span>
        )}
        <span className={styles.articleBreadcrumbSeparator}>
          <ChevronRightIcon width={12} height={12} fill='currentColor' />
        </span>
        <span aria-current='page'>{data.title}</span>
        {/* PRD 5.7: new articles carry the pill in breadcrumbs too. */}
        {data.is_new && <span className={styles.helpNewPill}>New</span>}
      </nav>

      <p className={styles.articleTagline}>{data.tagline}</p>

      {data.open_path && (
        <button
          type='button'
          className={styles.articleOpenButton}
          onClick={() => goTo(data.open_path!)}
        >
          <ExternalLinkIcon width={13} height={13} color='currentColor' />
          Open {data.open_path.label}
        </button>
      )}

      <div className={styles.articleBody}>
        {data.body.map((block, blockIndex) => (
          <HelpArticleBlock
            // eslint-disable-next-line react/no-array-index-key -- blocks have no id; the body is static per article
            key={blockIndex}
            block={block}
            onNavigate={goTo}
          />
        ))}
      </div>

      {/* Keyed by article so Previous / Next start from that article's own vote. */}
      <HelpArticleFeedback
        key={data.id}
        articleId={data.id}
        articleTitle={data.title}
        initialVote={data.my_vote}
        onAskAssistant={onAskAssistant}
        aiEnabled={aiEnabled}
      />

      <HelpArticlePager
        previous={data.previous ?? undefined}
        next={data.next ?? undefined}
        onOpenArticle={(article) => onOpenArticle(article, 'prevnext')}
      />

      {data.related.length > 0 && (
        <section className={styles.articleRelated}>
          <h3 className={styles.articleRelatedTitle}>Related articles</h3>
          <ul className={styles.articleRelatedList}>
            {data.related.map((article) => (
              <li key={article.id}>
                <button
                  type='button'
                  className={styles.articleRelatedLink}
                  onClick={() => onOpenArticle(article, 'related')}
                >
                  <span className={styles.articleRelatedLinkIcon}>
                    <LeftTailArrowIcon
                      size={14}
                      color='var(--light-blue)'
                      style={{ transform: 'scaleX(-1)' }}
                    />
                  </span>
                  <span>{article.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
};

export default HelpArticleView;
